import type { DirexRole, DirexStatus, IncidentKind } from "../../api/types";
import { k, t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";

/** Couleurs des incidents (pastilles, barres et étiquettes des croquis) : tactique rouge, technique gris,
 *  environnement jaune, en jetons `incident-*` d'index.css (R2) — utilisables en style en ligne et en SVG. */
export const KIND_COLOR: Record<IncidentKind, { fill: string; text: string; soft: string }> = {
  TAC: { fill: "var(--color-incident-tac)", text: "var(--color-surface)", soft: "var(--color-incident-tac-soft)" },
  TEC: { fill: "var(--color-incident-tec)", text: "var(--color-surface)", soft: "var(--color-incident-tec-soft)" },
  ENV: { fill: "var(--color-incident-env)", text: "var(--color-incident-env-ink)", soft: "var(--color-incident-env-soft)" },
};
// Libellés en clés françaises, traduits à l'affichage (`t(KIND_LABEL[type])`).
export const KIND_LABEL: Record<IncidentKind, string> = { TAC: k("Tactique"), TEC: k("Technique"), ENV: k("Environnement") };
export const STATUS_LABEL: Record<DirexStatus, string> = { prevu: k("Prévu"), injecte: k("Injecté"), traite: k("Traité") };
/** Statuts au pluriel (filtre « Statut » de la DIREX). */
export const STATUS_PLURAL: Record<DirexStatus, string> = { prevu: k("Prévus"), injecte: k("Injectés"), traite: k("Traités") };
export const UNASSIGNED = "__eventuel__";

/** Rôle d'un poste tel que le serveur le libelle (« Animateur », « Cellule S2 », « Observateur »), dans la langue
 *  de l'interface. */
export function roleLabel(role: DirexRole, label: string): string {
  const cell = role === "cellule" ? /^Cellule (.+)$/.exec(label) : null;
  return cell ? t("Cellule {cellule}", { cellule: term(cell[1]) }) : term(label);
}

/** Noms de poste donnés par le serveur lui-même (poste sans nom, injections automatiques) : traduits ; les autres
 *  sont les noms choisis pour les postes, laissés tels quels. */
const SERVER_TERMINALS = new Set([k("Poste sans nom"), k("Horloge d'exercice")]);

export function terminalName(name: string): string {
  return SERVER_TERMINALS.has(name) ? t(name) : name;
}

/** Plage de GDH d'un temps (bande de chronologie) : le mois et l'année communs aux deux bouts ne s'écrivent qu'une
 *  fois (« 030600B → 051200B OCT 26 »), ce qui la garde sur une ligne ; sinon les deux GDH entiers. */
export function dtgRange(start: string, end: string): [string, string] {
  const head = (dtg: string) => dtg.split(" ")[0] ?? dtg;
  const tail = (dtg: string) => dtg.slice(head(dtg).length);
  return tail(start) !== "" && tail(start) === tail(end) ? [`${head(start)} →`, end] : [`${start} →`, end];
}
